;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();import { Fragment, createElement, type ComponentType, type ReactNode } from "react"
import Image, { type ImageProps } from "next/image"
import Link from "next/link"

type RichTextNode = {
  type?: string
  text?: string
  spans?: Array<{
    start?: number
    end?: number
    type?: string
    data?: Record<string, unknown>
  }>
  data?: Record<string, unknown>
}

type RichTextComponent = ComponentType<{
  node?: RichTextNode
  children?: ReactNode
  [key: string]: unknown
}>

type RichTextComponents = Record<string, RichTextComponent | undefined>

function defaultTag(type: string): keyof React.JSX.IntrinsicElements {
  return type === "paragraph"
    ? "p"
    : /^heading[1-6]$/.test(type)
      ? (`h${type.at(-1)}` as keyof React.JSX.IntrinsicElements)
      : type === "preformatted"
        ? "pre"
        : type === "list-item" || type === "o-list-item"
          ? "li"
          : "span"
}

function renderSpans(
  text: string,
  spans: RichTextNode["spans"],
  components: RichTextComponents,
) {
  const validSpans = (spans || []).filter(
    (span) =>
      Number.isInteger(span.start) &&
      Number.isInteger(span.end) &&
      Number(span.start) >= 0 &&
      Number(span.end) <= text.length &&
      Number(span.end) > Number(span.start),
  )
  const boundaries = new Set([0, text.length])
  validSpans.forEach((span) => {
    boundaries.add(Number(span.start))
    boundaries.add(Number(span.end))
  })
  const points = [...boundaries].sort((a, b) => a - b)

  return points.slice(0, -1).map((start, index) => {
    const end = points[index + 1]
    let children: ReactNode = text.slice(start, end)
    const active = validSpans.filter(
      (span) => Number(span.start) <= start && Number(span.end) >= end,
    )
    active.forEach((span, spanIndex) => {
      if (span.type === "strong") {
        children = <strong key={`strong-${spanIndex}`}>{children}</strong>
      } else if (span.type === "em") {
        children = <em key={`em-${spanIndex}`}>{children}</em>
      } else if (span.type === "hyperlink") {
        const Hyperlink = components.hyperlink
        children = Hyperlink ? (
          <Hyperlink
            key={`link-${spanIndex}`}
            node={{ type: "hyperlink", data: span.data }}
          >
            {children}
          </Hyperlink>
        ) : (
          <span key={`link-${spanIndex}`}>{children}</span>
        )
      }
    })
    return <Fragment key={`${start}-${end}`}>{children}</Fragment>
  })
}

function renderNode(
  node: RichTextNode,
  index: number,
  components: RichTextComponents,
) {
  const type = node.type || "paragraph"
  const children = renderSpans(node.text || "", node.spans, components)
  const Component = components[type]
  return Component
    ? createElement(Component, { key: `${type}-${index}`, node }, children)
    : createElement(defaultTag(type), { key: `${type}-${index}` }, children)
}

export function ContentRichText({
  field,
  components = {},
}: {
  field?: readonly unknown[] | null
  components?: RichTextComponents
}) {
  if (!field?.length) return null
  const nodes = field as RichTextNode[]
  const output: ReactNode[] = []

  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index]
    const isListItem = node.type === "list-item" || node.type === "o-list-item"
    if (!isListItem) {
      output.push(renderNode(node, index, components))
      continue
    }

    const ordered = node.type === "o-list-item"
    const items: ReactNode[] = []
    while (
      index < nodes.length &&
      nodes[index].type === (ordered ? "o-list-item" : "list-item")
    ) {
      items.push(renderNode(nodes[index], index, components))
      index += 1
    }
    index -= 1
    const List = components[ordered ? "oList" : "list"]
    output.push(
      List
        ? createElement(List, { key: `list-${index}` }, items)
        : createElement(ordered ? "ol" : "ul", { key: `list-${index}` }, items),
    )
  }

  return <>{output}</>
}

type ContentLinkField = {
  url?: string | null
  target?: string | null
  text?: string | null
}

export function ContentLink({
  field,
  children,
  ...props
}: {
  field?: ContentLinkField | null
  children?: ReactNode
  [key: string]: unknown
}) {
  const href = field?.url
  if (!href) return null
  const content = children ?? field?.text ?? href
  if (/^https?:\/\//.test(href) || href.startsWith("//")) {
    return (
      <a
        href={href}
        target={field?.target || undefined}
        rel={field?.target === "_blank" ? "noopener noreferrer" : undefined}
        {...props}
      >
        {content}
      </a>
    )
  }
  return (
    <Link href={href} target={field?.target || undefined} {...props}>
      {content}
    </Link>
  )
}

export function ContentImage({
  field,
  imgixParams: _imgixParams,
  ...props
}: Omit<ImageProps, "src" | "alt"> & {
  field?: {
    url?: string | null
    alt?: string | null
    dimensions?: { width?: number; height?: number }
  } | null
  imgixParams?: Record<string, unknown>
}) {
  if (!field?.url) return null
  return (
    <Image
      src={field.url}
      alt={field.alt || ""}
      unoptimized={isPresignedUrl(field.url)}
      {...props}
    />
  )
}

/**
 * A presigned S3 GET (Article Studio draft media) expires within minutes and
 * is signed for one exact URL. Handing it to the image optimizer would cache
 * a copy under the signed URL and re-fetch it after expiry; render such
 * images as-is instead. Published media never carries a signature.
 */
export function isPresignedUrl(url: string | null | undefined): boolean {
  return typeof url === "string" && /[?&]X-Amz-Signature=/.test(url)
}

export const contentFieldIsFilled = {
  link(field: unknown): field is ContentLinkField {
    return Boolean(
      field &&
      typeof field === "object" &&
      "url" in field &&
      typeof (field as ContentLinkField).url === "string" &&
      (field as ContentLinkField).url,
    )
  },
}
